import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import Deferred from '../utils/Deferred';

export const PatternEditorContext = createContext({
  editorPattern: null,
  setEditorPattern: () => {},
  editNewPattern: () => {},
  hasDirtyPattern: false,
  setHasDirtyPattern: () => {},
  dirtyPatternModalShown: false,
  dirtyPatternModalDeferred: null,
  doOrConfirmIfDirty: () => {},
});

const getDefaultPattern = () => ({
  name: '',
  cropSequence: [
    { spacingFt: 10, crop: null },
    { spacingFt: 10, crop: null },
  ],
});

export const PatternEditorContextProvider = ({ children }) => {
  const [editorPattern, setEditorPattern] = useState(null);
  const [hasDirtyPattern, setHasDirtyPattern] = useState(false);
  const [dirtyPatternModalDeferred, setDirtyPatternModalDeferred] = useState(
    null,
  );

  const editNewPattern = () => setEditorPattern(getDefaultPattern());

  // If the pattern is dirty, show the modal and defer the function until the
  // user confirms.
  const doOrConfirmIfDirty = useCallback(
    (fn) => {
      if (hasDirtyPattern) {
        const deferred = new Deferred();
        setDirtyPatternModalDeferred(deferred);
        deferred.promise
          .then(fn)
          .catch(() => {
            // Do nothing, rejection is intentional.
          })
          .finally(() => {
            setDirtyPatternModalDeferred(null);
          });
      } else {
        fn();
      }
    },
    [hasDirtyPattern],
  );

  const value = useMemo(
    () => ({
      editorPattern,
      setEditorPattern,
      editNewPattern,
      setHasDirtyPattern,
      dirtyPatternModalShown: !!dirtyPatternModalDeferred,
      dirtyPatternModalDeferred,
      doOrConfirmIfDirty,
    }),
    [dirtyPatternModalDeferred, doOrConfirmIfDirty, editorPattern],
  );

  return (
    <PatternEditorContext.Provider value={value}>
      {children}
    </PatternEditorContext.Provider>
  );
};

export const usePatternEditorContext = () => useContext(PatternEditorContext);
